/**
 * Signer Policy DTOs (Issue #1304)
 *
 * A `SignerPolicy` declares which parties MUST sign a term sheet envelope,
 * in what order, and where each signer's identity should be resolved from.
 * Term sheet templates (Entity-Framework-backed) store a `SignerPolicy` on
 * the `signerPolicy` field; the party resolver in #1305 reads it to build
 * the concrete list of `SigningRecipient`s for a given case.
 *
 * Example: "two individual borrowers + one credit-manager counter-sign":
 *   [
 *     { role: 'borrower',       cardinality: 'many',     signingOrder: 1, counterSign: false, source: 'case.borrowers' },
 *     { role: 'credit_manager', cardinality: 'one',      signingOrder: 2, counterSign: true,  source: 'case.creditManager' },
 *   ]
 */

import { z } from 'zod';
import { SignerRoleSchema } from '../enums/signer-role';

/**
 * Where the party resolver should look to find each concrete signer.
 * `manual` means the envelope creator supplies the recipient directly.
 */
export const SignerPolicySourceSchema = z.enum([
  'case.assignedTo',
  'case.creditManager',
  'case.borrowers',
  'case.guarantors',
  'case.entitySignatory',
  'manual',
]);

export type SignerPolicySource = z.infer<typeof SignerPolicySourceSchema>;

/**
 * Cardinality of a policy item:
 * - `one`       → exactly one recipient must be produced (e.g., credit manager)
 * - `many`      → one or more recipients (e.g., multiple co-borrowers)
 * - `optional`  → zero or more recipients (e.g., guarantor only when present)
 */
export const SignerPolicyCardinalitySchema = z.enum(['one', 'many', 'optional']);

export type SignerPolicyCardinality = z.infer<typeof SignerPolicyCardinalitySchema>;

/**
 * A single signer policy item describing one required role in the envelope.
 *
 * Invariant (enforced by superRefine): an item with `cardinality: 'one'` or
 * `cardinality: 'many'` MUST declare a `source` unless `source` is `'manual'`.
 * A policy item that omits `source` while claiming to produce signers
 * automatically is a silent failure at #1305 resolver time — reject at
 * creation time instead.
 */
export const SignerPolicyItemSchema = z
  .object({
    role: SignerRoleSchema,
    cardinality: SignerPolicyCardinalitySchema,
    /**
     * 1-based signing order. Multiple items may share an order value to enable
     * parallel signing in the future (MVP enforces sequential ordering).
     */
    signingOrder: z.number().int().min(1).max(20),
    /** Counter-sign slot (lender executes after all consumer signers complete). */
    counterSign: z.boolean().default(false),
    /** Where the party resolver should look for the concrete signer. */
    source: SignerPolicySourceSchema.optional(),
  })
  .superRefine((item, ctx) => {
    // 'optional' cardinality may have no source (the resolver skips it if absent).
    // 'one' / 'many' must always name a source so the resolver has somewhere to look.
    if ((item.cardinality === 'one' || item.cardinality === 'many') && !item.source) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['source'],
        message: `SignerPolicyItem with cardinality "${item.cardinality}" must declare a source (e.g. 'case.borrowers', 'manual'). Role: ${item.role}`,
      });
    }
  });

export type SignerPolicyItem = z.infer<typeof SignerPolicyItemSchema>;

/**
 * A complete signer policy: an ordered list of policy items.
 * Empty array means "no signers required" (draft/preview-only template).
 */
export const SignerPolicySchema = z.array(SignerPolicyItemSchema).max(20);

export type SignerPolicy = z.infer<typeof SignerPolicySchema>;

/**
 * Default signer policy for the classic two-party flow:
 * one borrower signs first, one lender signer counter-signs.
 */
export const DEFAULT_SIGNER_POLICY: SignerPolicy = [
  {
    role: 'borrower',
    cardinality: 'one',
    signingOrder: 1,
    counterSign: false,
    source: 'case.borrowers',
  },
  {
    role: 'lender_signer',
    cardinality: 'one',
    signingOrder: 2,
    counterSign: true,
    source: 'case.assignedTo',
  },
];
